import { useState, useEffect, useCallback } from 'react';
import { api, Article, ArticleFilters, ArticleDocumentType } from '../lib/api';

const DOCUMENT_TYPES: { value: ArticleDocumentType; label: string }[] = [
  { value: 'article', label: 'Article' },
  { value: 'research', label: 'Research' },
  { value: 'speech', label: 'Speech' },
  { value: 'earnings_call', label: 'Earnings Call' },
  { value: 'policy_doc', label: 'Policy Document' },
  { value: 'other', label: 'Other' },
];

const TONE_COLORS: Record<string, string> = {
  bearish: '#ef4444',
  bullish: '#22c55e',
  cautious: '#f59e0b',
  alarmist: '#dc2626',
  measured: '#3b82f6',
  contrarian: '#8b5cf6',
  neutral: '#6b7280',
};

export function MarketKnowledge() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Form state
  const [sourcePaste, setSourcePaste] = useState('');
  const [headline, setHeadline] = useState('');
  const [sourcePublication, setSourcePublication] = useState('');
  const [author, setAuthor] = useState('');
  const [publicationDate, setPublicationDate] = useState('');
  const [url, setUrl] = useState('');
  const [documentType, setDocumentType] = useState<ArticleDocumentType>('article');
  const [curatorNote, setCuratorNote] = useState('');

  // Auto-detected state
  const [autoDetected, setAutoDetected] = useState<{
    headline?: boolean;
    source_publication?: boolean;
    author?: boolean;
    publication_date?: boolean;
    url?: boolean;
  }>({});
  const [extracting, setExtracting] = useState(false);
  const [tickersMentioned, setTickersMentioned] = useState<string[]>([]);

  // Articles browser state
  const [articles, setArticles] = useState<Article[]>([]);
  const [totalArticles, setTotalArticles] = useState(0);
  const [articlesLoading, setArticlesLoading] = useState(true);
  const [expandedArticle, setExpandedArticle] = useState<string | null>(null);
  const [articleDetail, setArticleDetail] = useState<{
    article: Article;
    extraction: any;
  } | null>(null);
  const [filters, setFilters] = useState<ArticleFilters>({
    sort_by: 'submission_desc',
    limit: 30,
  });

  // Source publications for filter
  const [sourcePublications, setSourcePublications] = useState<string[]>([]);

  // Load articles and source publications
  useEffect(() => {
    loadArticles();
    loadSourcePublications();
  }, [filters]);

  const loadArticles = async () => {
    setArticlesLoading(true);
    try {
      const result = await api.listArticles(filters);
      setArticles(result.articles);
      setTotalArticles(result.total);
    } catch (err) {
      console.error('Failed to load articles:', err);
    } finally {
      setArticlesLoading(false);
    }
  };

  const loadSourcePublications = async () => {
    try {
      const sources = await api.listSourcePublications();
      setSourcePublications(sources);
    } catch {
      // Silent fail
    }
  };

  // Auto-extract metadata when text is pasted
  const handlePasteChange = useCallback(
    async (text: string) => {
      setSourcePaste(text);
      setAutoDetected({});
      setTickersMentioned([]);

      if (text.length > 200) {
        setExtracting(true);
        try {
          const metadata = await api.extractArticlePasteMetadata(text);
          const detected: typeof autoDetected = {};

          if (metadata.headline && !headline) {
            setHeadline(metadata.headline);
            detected.headline = true;
          }
          if (metadata.source_publication && !sourcePublication) {
            setSourcePublication(metadata.source_publication);
            detected.source_publication = true;
          }
          if (metadata.author && !author) {
            setAuthor(metadata.author);
            detected.author = true;
          }
          if (metadata.publication_date && !publicationDate) {
            setPublicationDate(metadata.publication_date);
            detected.publication_date = true;
          }
          if (metadata.url && !url) {
            setUrl(metadata.url);
            detected.url = true;
          }
          if (metadata.tickers_mentioned?.length > 0) {
            setTickersMentioned(metadata.tickers_mentioned);
          }

          setAutoDetected(detected);
        } catch {
          // Silent fail for metadata extraction
        } finally {
          setExtracting(false);
        }
      }
    },
    [headline, sourcePublication, author, publicationDate, url]
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      if (!sourcePaste.trim()) throw new Error('Article text is required');

      const result = await api.submitArticle({
        source_paste: sourcePaste.trim(),
        headline: headline || undefined,
        source_publication: sourcePublication || undefined,
        author: author || undefined,
        publication_date: publicationDate || undefined,
        url: url || undefined,
        document_type: documentType,
        curator_note: curatorNote || undefined,
      });

      // Clear form
      setSourcePaste('');
      setHeadline('');
      setSourcePublication('');
      setAuthor('');
      setPublicationDate('');
      setUrl('');
      setDocumentType('article');
      setCuratorNote('');
      setAutoDetected({});
      setTickersMentioned([]);
      setSuccess(result.message);

      // Reload articles
      loadArticles();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Submission failed');
    } finally {
      setLoading(false);
    }
  };

  const handleExpandArticle = async (articleId: string) => {
    if (expandedArticle === articleId) {
      setExpandedArticle(null);
      setArticleDetail(null);
      return;
    }

    setExpandedArticle(articleId);
    try {
      const detail = await api.getArticle(articleId);
      setArticleDetail(detail);
    } catch {
      setError('Failed to load article details');
    }
  };

  const handleFeedbackUpdate = async (articleId: string, status: string) => {
    try {
      await api.updateArticleExtractionFeedback(articleId, status);
      loadArticles();
      if (expandedArticle === articleId) {
        const detail = await api.getArticle(articleId);
        setArticleDetail(detail);
      }
    } catch {
      setError('Failed to update feedback');
    }
  };

  const handleDeleteArticle = async (articleId: string) => {
    if (!confirm('Delete this article? This cannot be undone.')) return;
    try {
      await api.deleteArticle(articleId);
      setSuccess('Article deleted');
      loadArticles();
      if (expandedArticle === articleId) {
        setExpandedArticle(null);
        setArticleDetail(null);
      }
    } catch {
      setError('Failed to delete article');
    }
  };

  const parseJSON = (str: string | null): any[] => {
    if (!str) return [];
    try {
      return JSON.parse(str);
    } catch {
      return [];
    }
  };

  const getStatusClass = (status: string) => `status-${status}`;

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">Submit Market Knowledge</h1>
        <p className="page-subtitle">
          Paste articles from Bloomberg or other sources for structured extraction
        </p>
      </div>

      {error && <div className="error-message">{error}</div>}
      {success && <div className="success-message">{success}</div>}

      {/* Submission Form */}
      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label className="form-label">
            Article Text
            {extracting && (
              <span className="text-muted text-small" style={{ marginLeft: '0.5rem' }}>
                Extracting metadata...
              </span>
            )}
          </label>
          <textarea
            className="form-textarea"
            value={sourcePaste}
            onChange={(e) => handlePasteChange(e.target.value)}
            placeholder="Paste the full article text here..."
            style={{ minHeight: '250px' }}
          />
          {tickersMentioned.length > 0 && (
            <div className="text-small text-muted mt-half">
              Tickers detected: {tickersMentioned.join(', ')}
            </div>
          )}
        </div>

        <div className="form-row">
          <div className="form-group" style={{ flex: 2 }}>
            <label className="form-label">
              Headline
              {autoDetected.headline && <span className="auto-detected-badge">Auto-detected</span>}
            </label>
            <input
              type="text"
              className={`form-input ${autoDetected.headline ? 'auto-detected' : ''}`}
              value={headline}
              onChange={(e) => {
                setHeadline(e.target.value);
                setAutoDetected((prev) => ({ ...prev, headline: false }));
              }}
              placeholder="Article headline"
            />
          </div>

          <div className="form-group" style={{ flex: 1 }}>
            <label className="form-label">
              Source
              {autoDetected.source_publication && (
                <span className="auto-detected-badge">Auto-detected</span>
              )}
            </label>
            <input
              type="text"
              className={`form-input ${autoDetected.source_publication ? 'auto-detected' : ''}`}
              value={sourcePublication}
              onChange={(e) => {
                setSourcePublication(e.target.value);
                setAutoDetected((prev) => ({ ...prev, source_publication: false }));
              }}
              placeholder="e.g., Bloomberg"
            />
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label className="form-label">
              Author
              {autoDetected.author && <span className="auto-detected-badge">Auto-detected</span>}
            </label>
            <input
              type="text"
              className={`form-input ${autoDetected.author ? 'auto-detected' : ''}`}
              value={author}
              onChange={(e) => {
                setAuthor(e.target.value);
                setAutoDetected((prev) => ({ ...prev, author: false }));
              }}
              placeholder="Author name"
            />
          </div>

          <div className="form-group">
            <label className="form-label">
              Publication Date
              {autoDetected.publication_date && (
                <span className="auto-detected-badge">Auto-detected</span>
              )}
            </label>
            <input
              type="date"
              className={`form-input ${autoDetected.publication_date ? 'auto-detected' : ''}`}
              value={publicationDate}
              onChange={(e) => {
                setPublicationDate(e.target.value);
                setAutoDetected((prev) => ({ ...prev, publication_date: false }));
              }}
            />
          </div>

          <div className="form-group">
            <label className="form-label">Document Type</label>
            <select
              className="form-select"
              value={documentType}
              onChange={(e) => setDocumentType(e.target.value as ArticleDocumentType)}
            >
              {DOCUMENT_TYPES.map((t) => (
                <option key={t.value} value={t.value}>
                  {t.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="form-row">
          <div className="form-group" style={{ flex: 2 }}>
            <label className="form-label">
              URL
              {autoDetected.url && <span className="auto-detected-badge">Auto-detected</span>}
            </label>
            <input
              type="url"
              className={`form-input ${autoDetected.url ? 'auto-detected' : ''}`}
              value={url}
              onChange={(e) => {
                setUrl(e.target.value);
                setAutoDetected((prev) => ({ ...prev, url: false }));
              }}
              placeholder="https://..."
            />
          </div>

          <div className="form-group" style={{ flex: 1 }}>
            <label className="form-label">Curator Note (optional)</label>
            <input
              type="text"
              className="form-input"
              value={curatorNote}
              onChange={(e) => setCuratorNote(e.target.value)}
              placeholder="Why this article matters"
            />
          </div>
        </div>

        <button type="submit" className="btn btn-primary" disabled={loading}>
          {loading ? 'Submitting...' : 'Submit & Extract'}
        </button>
      </form>

      {/* Articles Browser */}
      <div className="mt-3">
        <div className="card-header">
          <h2 className="card-title">Recent Articles</h2>
          <div className="flex gap-1">
            <select
              className="form-select form-select-small"
              value={filters.source_publication || ''}
              onChange={(e) =>
                setFilters((f) => ({
                  ...f,
                  source_publication: e.target.value || undefined,
                }))
              }
            >
              <option value="">All Sources</option>
              {sourcePublications.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>

            <select
              className="form-select form-select-small"
              value={filters.document_type || ''}
              onChange={(e) =>
                setFilters((f) => ({
                  ...f,
                  document_type: (e.target.value as ArticleDocumentType) || undefined,
                }))
              }
            >
              <option value="">All Types</option>
              {DOCUMENT_TYPES.map((t) => (
                <option key={t.value} value={t.value}>
                  {t.label}
                </option>
              ))}
            </select>

            <select
              className="form-select form-select-small"
              value={filters.processing_status || ''}
              onChange={(e) =>
                setFilters((f) => ({
                  ...f,
                  processing_status: (e.target.value as any) || undefined,
                }))
              }
            >
              <option value="">All Status</option>
              <option value="pending">Pending</option>
              <option value="extracting">Extracting</option>
              <option value="ready">Ready</option>
              <option value="error">Error</option>
            </select>

            <select
              className="form-select form-select-small"
              value={filters.sort_by || 'submission_desc'}
              onChange={(e) =>
                setFilters((f) => ({
                  ...f,
                  sort_by: e.target.value as any,
                }))
              }
            >
              <option value="submission_desc">Newest First</option>
              <option value="submission_asc">Oldest First</option>
              <option value="publication_desc">Publication Date (Newest)</option>
              <option value="publication_asc">Publication Date (Oldest)</option>
              <option value="source">By Source</option>
            </select>
          </div>
        </div>

        <div className="text-small text-muted mb-1">
          {totalArticles} article{totalArticles !== 1 ? 's' : ''}
        </div>

        {articlesLoading ? (
          <div className="text-muted">Loading...</div>
        ) : articles.length === 0 ? (
          <div className="text-muted">No articles yet</div>
        ) : (
          <div className="status-list">
            {articles.map((article) => (
              <div key={article.article_id}>
                <div
                  className="status-item"
                  style={{ cursor: 'pointer' }}
                  onClick={() => handleExpandArticle(article.article_id)}
                >
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 500 }}>
                      {article.headline || 'Untitled'}
                      {article.source_publication && (
                        <span className="text-muted text-small" style={{ marginLeft: '0.5rem' }}>
                          {article.source_publication}
                        </span>
                      )}
                    </div>
                    <div className="text-small text-muted">
                      {article.publication_date || 'No date'} &bull;{' '}
                      {DOCUMENT_TYPES.find((t) => t.value === article.document_type)?.label ||
                        article.document_type}
                      {article.author && ` &bull; ${article.author}`}
                    </div>
                    {article.summary && (
                      <div
                        className="text-small"
                        style={{ marginTop: '0.25rem', color: 'var(--text-secondary)' }}
                      >
                        {article.summary.length > 200
                          ? article.summary.slice(0, 200) + '...'
                          : article.summary}
                      </div>
                    )}
                  </div>
                  <div className="flex gap-1 items-center">
                    <span className={getStatusClass(article.processing_status)}>
                      {article.processing_status}
                    </span>
                    {article.extraction_feedback_status && (
                      <span
                        className={`status-${article.extraction_feedback_status}`}
                        style={{ fontSize: '0.7rem' }}
                      >
                        {article.extraction_feedback_status}
                      </span>
                    )}
                    <span style={{ transform: expandedArticle === article.article_id ? 'rotate(180deg)' : 'none' }}>
                      &#9660;
                    </span>
                  </div>
                </div>

                {/* Expanded Detail */}
                {expandedArticle === article.article_id && articleDetail && (
                  <div className="card" style={{ marginTop: '0.5rem', marginBottom: '1rem' }}>
                    {articleDetail.extraction ? (
                      <>
                        {/* Summary */}
                        <div className="form-group">
                          <label className="form-label text-small">Summary</label>
                          <div>{articleDetail.extraction.summary || 'No summary'}</div>
                        </div>

                        {/* Themes */}
                        <div className="form-group">
                          <label className="form-label text-small">Themes</label>
                          <div className="flex gap-half flex-wrap">
                            {parseJSON(articleDetail.extraction.themes).map((theme, i) => (
                              <span key={i} className="tag">
                                {theme}
                              </span>
                            ))}
                            {parseJSON(articleDetail.extraction.themes).length === 0 && (
                              <span className="text-muted">None</span>
                            )}
                          </div>
                        </div>

                        {/* Tone & Quality */}
                        <div className="form-row">
                          <div className="form-group">
                            <label className="form-label text-small">Tone</label>
                            <span
                              style={{
                                color: TONE_COLORS[articleDetail.extraction.tone] || 'inherit',
                                fontWeight: 500,
                              }}
                            >
                              {articleDetail.extraction.tone || '-'}
                            </span>
                          </div>
                          <div className="form-group">
                            <label className="form-label text-small">Quality</label>
                            <span>
                              {articleDetail.extraction.quality_flag?.replace(/_/g, ' ') || '-'}
                            </span>
                          </div>
                          <div className="form-group">
                            <label className="form-label text-small">Time Horizon</label>
                            <span>
                              {articleDetail.extraction.time_horizon?.replace(/_/g, ' ') || '-'}
                            </span>
                          </div>
                          <div className="form-group">
                            <label className="form-label text-small">Confidence</label>
                            <span>{articleDetail.extraction.extraction_confidence || '-'}</span>
                          </div>
                        </div>

                        {/* Asset Classes & Sectors */}
                        <div className="form-row">
                          <div className="form-group">
                            <label className="form-label text-small">Asset Classes</label>
                            <div className="flex gap-half flex-wrap">
                              {parseJSON(articleDetail.extraction.asset_classes).map((ac, i) => (
                                <span key={i} className="tag">
                                  {ac}
                                </span>
                              ))}
                              {parseJSON(articleDetail.extraction.asset_classes).length === 0 && (
                                <span className="text-muted">-</span>
                              )}
                            </div>
                          </div>
                          <div className="form-group">
                            <label className="form-label text-small">Sectors</label>
                            <div className="flex gap-half flex-wrap">
                              {parseJSON(articleDetail.extraction.sectors).map((s, i) => (
                                <span key={i} className="tag">
                                  {s}
                                </span>
                              ))}
                              {parseJSON(articleDetail.extraction.sectors).length === 0 && (
                                <span className="text-muted">-</span>
                              )}
                            </div>
                          </div>
                        </div>

                        {/* Tickers & Geography */}
                        <div className="form-row">
                          <div className="form-group">
                            <label className="form-label text-small">Tickers</label>
                            <div className="flex gap-half flex-wrap">
                              {parseJSON(articleDetail.extraction.tickers_mentioned).map((t, i) => (
                                <span key={i} className="tag">
                                  {t}
                                </span>
                              ))}
                              {parseJSON(articleDetail.extraction.tickers_mentioned).length ===
                                0 && <span className="text-muted">-</span>}
                            </div>
                          </div>
                          <div className="form-group">
                            <label className="form-label text-small">Geography</label>
                            <div className="flex gap-half flex-wrap">
                              {parseJSON(articleDetail.extraction.geography).map((g, i) => (
                                <span key={i} className="tag">
                                  {g.toUpperCase()}
                                </span>
                              ))}
                              {parseJSON(articleDetail.extraction.geography).length === 0 && (
                                <span className="text-muted">-</span>
                              )}
                            </div>
                          </div>
                        </div>

                        {/* Key Claims */}
                        {parseJSON(articleDetail.extraction.key_claims).length > 0 && (
                          <div className="form-group">
                            <label className="form-label text-small">Key Claims</label>
                            <ul style={{ margin: 0, paddingLeft: '1.25rem' }}>
                              {parseJSON(articleDetail.extraction.key_claims).map(
                                (claim: any, i: number) => (
                                  <li key={i} className="text-small">
                                    <span style={{ fontWeight: 500 }}>[{claim.claim_type}]</span>{' '}
                                    {claim.claim_text}
                                  </li>
                                )
                              )}
                            </ul>
                          </div>
                        )}

                        {/* Advisor Implications */}
                        {articleDetail.extraction.advisor_implications && (
                          <div className="form-group">
                            <label className="form-label text-small">Advisor Implications</label>
                            <div className="text-small">
                              {articleDetail.extraction.advisor_implications}
                            </div>
                          </div>
                        )}

                        {/* Sources Cited */}
                        {parseJSON(articleDetail.extraction.sources_cited).length > 0 && (
                          <div className="form-group">
                            <label className="form-label text-small">Sources Cited</label>
                            <div className="text-small">
                              {parseJSON(articleDetail.extraction.sources_cited).join(', ')}
                            </div>
                          </div>
                        )}

                        {/* Market Mood */}
                        <div className="form-group">
                          <label className="form-label text-small">Market Mood</label>
                          <span
                            className={`status-${articleDetail.extraction.market_mood || 'unknown'}`}
                          >
                            {articleDetail.extraction.market_mood || 'unknown'}
                          </span>
                        </div>
                      </>
                    ) : (
                      <div className="text-muted">
                        {article.processing_status === 'error'
                          ? `Error: ${article.error_message || 'Unknown error'}`
                          : 'Extraction pending...'}
                      </div>
                    )}

                    {/* Actions */}
                    <div
                      className="flex gap-1 mt-1"
                      style={{ borderTop: '1px solid var(--border)', paddingTop: '1rem' }}
                    >
                      {articleDetail.extraction && (
                        <>
                          <button
                            className={`btn btn-small ${
                              articleDetail.extraction.feedback_status === 'valid'
                                ? 'btn-primary'
                                : 'btn-secondary'
                            }`}
                            onClick={() => handleFeedbackUpdate(article.article_id, 'valid')}
                          >
                            Valid
                          </button>
                          <button
                            className={`btn btn-small ${
                              articleDetail.extraction.feedback_status === 'invalid'
                                ? 'btn-primary'
                                : 'btn-secondary'
                            }`}
                            onClick={() => handleFeedbackUpdate(article.article_id, 'invalid')}
                          >
                            Invalid
                          </button>
                          <button
                            className={`btn btn-small ${
                              articleDetail.extraction.feedback_status === 'needs_revision'
                                ? 'btn-primary'
                                : 'btn-secondary'
                            }`}
                            onClick={() => handleFeedbackUpdate(article.article_id, 'needs_revision')}
                          >
                            Needs Revision
                          </button>
                        </>
                      )}
                      <div style={{ flex: 1 }} />
                      <button
                        className="btn btn-small btn-secondary"
                        style={{ color: 'var(--error)' }}
                        onClick={() => handleDeleteArticle(article.article_id)}
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
